import React, { createContext, useContext, useState, useEffect } from 'react';

// ⚠️ IMPORTANT : Ajustez ce chemin selon votre structure
// import { db } from '../firebase/firebase';  // Option 1 (par défaut)
// import { db } from '../config/firebase';  // Option 2
import { db } from '../firebase';         // Option 3

import { 
  collection, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  query,
  orderBy,
  where,
  Timestamp
} from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { logger, performance, logFirebaseError } from '../utils/logger';
// 🆕 SÉCURITÉ : Import Cloud Functions pour emails
import { getFunctions, httpsCallable } from 'firebase/functions';


const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { currentUser } = useAuth(); // ✅ MODIFICATION : Récupérer currentUser

  // État des données
  const [data, setData] = useState({
    members: [],
    donations: [],
    expenses: [],
    programmes: [],
    fihirana: [],
    users: [],
    churchJournal: [],
    settings: {
      churchName: 'VOMM FJKM Hannover',
      currency: 'EUR'
    }
  });

  const [loading, setLoading] = useState(true);

  // ✅ Charger les données SEULEMENT si utilisateur connecté, en parallèle pour optimiser
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        logger.log('🔄 Chargement optimisé des données...');
        logger.log('👤 Utilisateur connecté:', currentUser.name || currentUser.email);

        // 📌 Toutes les requêtes Firestore en parallèle
        const [
          membersSnap,
          donationsSnap,
          expensesSnap,
          programmesSnap,
          fihiranaSnap,
          usersSnap,
          journalSnap,
        ] = await Promise.all([
          getDocs(collection(db, 'members')),
          getDocs(query(collection(db, 'donations'), orderBy('date', 'desc'))),
          getDocs(query(collection(db, 'expenses'), orderBy('date', 'desc'))),
          getDocs(collection(db, 'programmes')),
          getDocs(collection(db, 'fihirana')),
          getDocs(collection(db, 'users')),
          getDocs(
            query(collection(db, 'churchJournal'), orderBy('createdAt', 'desc'))
          ),
        ]);

        // 📌 Conversion en une seule passe
        const membersData = membersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const donationsData = donationsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const expensesData = expensesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const programmesData = programmesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fihiranaData = fihiranaSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const usersData = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const journalData = journalSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // 📌 Un seul setData = beaucoup moins de re-render
        setData(prev => ({
          ...prev,
          members: membersData,
          donations: donationsData,
          expenses: expensesData,
          programmes: programmesData,
          fihirana: fihiranaData,
          users: usersData,
          churchJournal: journalData
        }));

        logger.log('⚡ Chargement optimisé terminé', {
          membres: membersData.length,
          donations: donationsData.length,
          dépenses: expensesData.length
        });

        setLoading(false);
      } catch (error) {
        logger.error('❌ Erreur lors du chargement des données:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser]); // ✅ MODIFICATION : Recharger quand currentUser change

  // ========== MEMBRES ==========

  const addMember = async (member) => {
    try {
      const memberData = {
        ...member,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'members'), memberData);
      
      const newMember = {
        id: docRef.id,
        ...memberData
      };

      setData(prev => ({
        ...prev,
        members: [...prev.members, newMember]
      }));

      logger.log('✅ Membre ajouté:', docRef.id);
      return newMember;
    } catch (error) {
      logger.error('❌ Erreur ajout membre:', error);
      throw error;
    }
  };

  const updateMember = async (id, updates) => {
    try {
      const docRef = doc(db, 'members', id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });

      setData(prev => ({
        ...prev,
        members: prev.members.map(m => 
          m.id === id ? { ...m, ...updates } : m
        )
      }));

      logger.log('✅ Membre modifié:', id);
    } catch (error) {
      logger.error('❌ Erreur modification membre:', error);
      throw error;
    }
  };

  const deleteMember = async (id) => {
    try {
      await deleteDoc(doc(db, 'members', id));

      setData(prev => ({
        ...prev,
        members: prev.members.filter(m => m.id !== id)
      }));

      logger.log('✅ Membre supprimé:', id);
    } catch (error) {
      logger.error('❌ Erreur suppression membre:', error);
      throw error;
    }
  };

  // ========== DONATIONS ==========

  const addDonation = async (donation) => {
    try {
      const donationData = {
        ...donation,
        date: donation.date || Timestamp.now(),
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'donations'), donationData);
      
      const newDonation = {
        id: docRef.id,
        ...donationData
      };

      setData(prev => ({
        ...prev,
        donations: [newDonation, ...prev.donations]
      }));

      logger.log('✅ Don ajouté:', docRef.id);
      return newDonation;
    } catch (error) {
      logger.error('❌ Erreur ajout don:', error);
      throw error;
    }
  };

  const updateDonation = async (id, updates) => {
    try {
      const docRef = doc(db, 'donations', id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });

      setData(prev => ({
        ...prev,
        donations: prev.donations.map(d => 
          d.id === id ? { ...d, ...updates } : d
        )
      }));

      logger.log('✅ Don modifié:', id);
    } catch (error) {
      logger.error('❌ Erreur modification don:', error);
      throw error;
    }
  };

  const deleteDonation = async (id) => {
    try {
      await deleteDoc(doc(db, 'donations', id));

      setData(prev => ({
        ...prev,
        donations: prev.donations.filter(d => d.id !== id)
      }));

      logger.log('✅ Don supprimé:', id);
    } catch (error) {
      logger.error('❌ Erreur suppression don:', error);
      throw error;
    }
  };

  // ========== EXPENSES ==========

  const addExpense = async (expense) => {
    try {
      const expenseData = {
        ...expense,
        date: expense.date || Timestamp.now(),
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'expenses'), expenseData);
      
      const newExpense = {
        id: docRef.id,
        ...expenseData
      };

      setData(prev => ({
        ...prev,
        expenses: [newExpense, ...prev.expenses]
      }));

      logger.log('✅ Dépense ajoutée:', docRef.id);
      return newExpense;
    } catch (error) {
      logger.error('❌ Erreur ajout dépense:', error);
      throw error;
    }
  };

  const updateExpense = async (id, updates) => {
    try {
      const docRef = doc(db, 'expenses', id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });

      setData(prev => ({
        ...prev,
        expenses: prev.expenses.map(e => 
          e.id === id ? { ...e, ...updates } : e
        )
      }));

      logger.log('✅ Dépense modifiée:', id);
    } catch (error) {
      logger.error('❌ Erreur modification dépense:', error);
      throw error;
    }
  };

  const deleteExpense = async (id) => {
    try {
      await deleteDoc(doc(db, 'expenses', id));

      setData(prev => ({
        ...prev,
        expenses: prev.expenses.filter(e => e.id !== id)
      }));

      logger.log('✅ Dépense supprimée:', id);
    } catch (error) {
      logger.error('❌ Erreur suppression dépense:', error);
      throw error;
    }
  };

  // ========== CHURCH JOURNAL ==========

  const addJournalEntry = async (entry) => {
    try {
      const entryData = {
        ...entry,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'churchJournal'), entryData);
      
      const newEntry = {
        id: docRef.id,
        ...entryData
      };

      setData(prev => ({
        ...prev,
        churchJournal: [newEntry, ...prev.churchJournal]
      }));

      logger.log('✅ Entrée journal ajoutée:', docRef.id);
      return newEntry;
    } catch (error) {
      logger.error('❌ Erreur ajout entrée journal:', error);
      throw error;
    }
  };

  const updateJournalEntry = async (id, updates) => {
    try {
      const docRef = doc(db, 'churchJournal', id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });

      setData(prev => ({
        ...prev,
        churchJournal: prev.churchJournal.map(j => 
          j.id === id ? { ...j, ...updates } : j
        )
      }));

      logger.log('✅ Entrée journal modifiée:', id);
    } catch (error) {
      logger.error('❌ Erreur modification entrée journal:', error);
      throw error;
    }
  };

  const deleteJournalEntry = async (id) => {
    try {
      await deleteDoc(doc(db, 'churchJournal', id));

      setData(prev => ({
        ...prev,
        churchJournal: prev.churchJournal.filter(j => j.id !== id)
      }));

      logger.log('✅ Entrée journal supprimée:', id);
    } catch (error) {
      logger.error('❌ Erreur suppression entrée journal:', error);
      throw error;
    }
  };

  // ========== PROGRAMMES ==========

  const addProgramme = async (programme) => {
    try {
      const programmeData = {
        ...programme,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'programmes'), programmeData);
      
      const newProgramme = {
        id: docRef.id,
        ...programmeData
      };

      setData(prev => ({
        ...prev,
        programmes: [...prev.programmes, newProgramme]
      }));

      logger.log('✅ Programme ajouté:', docRef.id);
      return newProgramme;
    } catch (error) {
      logger.error('❌ Erreur ajout programme:', error);
      throw error;
    }
  };

  const updateProgramme = async (id, updates) => {
    try {
      const docRef = doc(db, 'programmes', id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });

      setData(prev => ({
        ...prev,
        programmes: prev.programmes.map(p => 
          p.id === id ? { ...p, ...updates } : p
        )
      }));

      logger.log('✅ Programme modifié:', id);
    } catch (error) {
      logger.error('❌ Erreur modification programme:', error);
      throw error;
    }
  };

  const deleteProgramme = async (id) => {
    try {
      await deleteDoc(doc(db, 'programmes', id));

      setData(prev => ({
        ...prev,
        programmes: prev.programmes.filter(p => p.id !== id)
      }));

      logger.log('✅ Programme supprimé:', id);
    } catch (error) {
      logger.error('❌ Erreur suppression programme:', error);
      throw error;
    }
  };

  // ========== USERS (GESTION UTILISATEURS) ==========

  // 🆕 CORRECTION : Fonction approveUser avec envoi d'email via Cloud Function
  const approveUser = async (userId) => {
    try {
      // 1. Récupérer les infos de l'utilisateur AVANT la mise à jour
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        logger.error('❌ Utilisateur non trouvé:', userId);
        return { success: false, message: 'Utilisateur non trouvé' };
      }
      
      const userData = userDoc.data();
      const userEmail = userData.email;
      const userName = userData.name;
      
      logger.log('📧 Utilisateur à approuver:', { email: userEmail, name: userName });
      
      // 2. Mettre à jour le statut dans Firestore
      const updates = {
        approved: true,
        status: 'approved',
        approvedAt: Timestamp.now(),
        approvedBy: currentUser?.email || 'admin'
      };
      
      await updateDoc(userRef, updates);

      // 3. Mettre à jour l'état local
      setData(prev => ({
        ...prev,
        users: prev.users.map(u => 
          u.id === userId ? { ...u, ...updates } : u
        )
      }));

      logger.log('✅ Utilisateur approuvé dans Firestore:', userId);
      
      // 4. 📧 Envoyer l'email d'approbation via Cloud Function (en arrière-plan, non-bloquant)
      setTimeout(async () => {
        try {
          logger.log('📧 Envoi email d\'approbation via Cloud Function à:', userEmail);
          
          // 🆕 Utiliser Cloud Function sécurisée
          const functions = getFunctions();
          const sendEmailFunction = httpsCallable(functions, 'sendEmail');
          
          const emailResult = await sendEmailFunction({
            to: userEmail,
            subject: 'Compte approuvé - VOMM FJKM Hannover',
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #10b981;">Félicitations ${userName} !</h2>
                <p>Votre compte a été approuvé par un administrateur.</p>
                <p>Vous pouvez maintenant vous connecter et accéder à toutes les fonctionnalités de l'application VOMM FJKM Hannover.</p>
                <div style="margin: 30px 0;">
                  <a href="https://vomm-fjkm.de" style="background-color: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">Se connecter maintenant</a>
                </div>
                <div style="background-color: #f3f4f6; padding: 15px; border-radius: 6px; margin: 20px 0;">
                  <p style="margin: 0; font-size: 14px; color: #6b7280;">
                    <strong>Vos accès :</strong><br>
                    Email : ${userEmail}<br>
                    Mot de passe : celui que vous avez défini lors de l'inscription
                  </p>
                </div>
                <p>Si vous avez des questions, n'hésitez pas à nous contacter.</p>
                <p style="margin-top: 30px; color: #6b7280; font-size: 14px;">
                  Cordialement,<br>
                  <strong>L'équipe VOMM FJKM Hannover</strong>
                </p>
              </div>
            `,
            name: userName
          });
          
          if (emailResult.data.success) {
            logger.log('✅ Email d\'approbation envoyé avec succès');
          }
        } catch (emailError) {
          logger.error('⚠️ Exception envoi email d\'approbation (non-bloquant):', emailError.message);
        }
      }, 100);

      return { success: true, message: 'Utilisateur approuvé avec succès' };
    } catch (error) {
      logger.error('❌ Erreur approbation utilisateur:', error);
      return { success: false, message: error.message };
    }
  };

  const rejectUser = async (userId) => {
    try {
      await deleteDoc(doc(db, 'users', userId));

      setData(prev => ({
        ...prev,
        users: prev.users.filter(u => u.id !== userId)
      }));

      logger.log('✅ Utilisateur rejeté et supprimé:', userId);
      return { success: true, message: 'Utilisateur rejeté avec succès' };
    } catch (error) {
      logger.error('❌ Erreur rejet utilisateur:', error);
      return { success: false, message: error.message };
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole,
        updatedAt: Timestamp.now()
      });

      setData(prev => ({
        ...prev,
        users: prev.users.map(u => 
          u.id === userId ? { ...u, role: newRole, updatedAt: Timestamp.now() } : u
        )
      }));

      logger.log('✅ Rôle utilisateur modifié:', userId, '→', newRole);
      return { success: true, message: 'Rôle modifié avec succès' };
    } catch (error) {
      logger.error('❌ Erreur modification rôle:', error);
      return { success: false, message: error.message };
    }
  };

  const deleteUser = async (userId) => {
    try {
      await deleteDoc(doc(db, 'users', userId));

      setData(prev => ({
        ...prev,
        users: prev.users.filter(u => u.id !== userId)
      }));

      logger.log('✅ Utilisateur supprimé:', userId);
      return { success: true, message: 'Utilisateur supprimé avec succès' };
    } catch (error) {
      logger.error('❌ Erreur suppression utilisateur:', error);
      return { success: false, message: error.message };
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      const userRef = doc(db, 'users', userId);
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      };
      
      await updateDoc(userRef, updateData);

      setData(prev => ({
        ...prev,
        users: prev.users.map(u => 
          u.id === userId ? { ...u, ...updateData } : u
        )
      }));

      logger.log('✅ Utilisateur mis à jour:', userId);
      return { success: true, message: 'Profil mis à jour avec succès' };
    } catch (error) {
      logger.error('❌ Erreur mise à jour utilisateur:', error);
      return { success: false, message: error.message };
    }
  };

  const addUser = async (userData) => {
    try {
      const newUserData = {
        ...userData,
        approved: false,
        status: 'pending', // 🆕 AJOUT : Définir status par défaut
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'users'), newUserData);
      
      const newUser = {
        id: docRef.id,
        ...newUserData
      };

      setData(prev => ({
        ...prev,
        users: [...prev.users, newUser]
      }));

      logger.log('✅ Utilisateur ajouté:', docRef.id);
      return newUser;
    } catch (error) {
      logger.error('❌ Erreur ajout utilisateur:', error);
      throw error;
    }
  };

  // ========== FIHIRANA ==========

  const addFihirana = async (fihirana) => {
    try {
      const fihiranaData = {
        ...fihirana,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'fihirana'), fihiranaData);
      
      const newFihirana = {
        id: docRef.id,
        ...fihiranaData
      };

      setData(prev => ({
        ...prev,
        fihirana: [...prev.fihirana, newFihirana]
      }));

      logger.log('✅ Fihirana ajouté:', docRef.id);
      return newFihirana;
    } catch (error) {
      logger.error('❌ Erreur ajout fihirana:', error);
      throw error;
    }
  };

  const updateFihirana = async (id, updates) => {
    try {
      const docRef = doc(db, 'fihirana', id);
      await updateDoc(docRef, updates);

      setData(prev => ({
        ...prev,
        fihirana: prev.fihirana.map(f => 
          f.id === id ? { ...f, ...updates } : f
        )
      }));

      logger.log('✅ Fihirana modifié:', id);
    } catch (error) {
      logger.error('❌ Erreur modification fihirana:', error);
      throw error;
    }
  };

  const deleteFihirana = async (id) => {
    try {
      await deleteDoc(doc(db, 'fihirana', id));

      setData(prev => ({
        ...prev,
        fihirana: prev.fihirana.filter(f => f.id !== id)
      }));

      logger.log('✅ Fihirana supprimé:', id);
    } catch (error) {
      logger.error('❌ Erreur suppression fihirana:', error);
      throw error;
    }
  };

  const value = {
    data,
    loading,
    // Membres
    addMember,
    updateMember,
    deleteMember,
    // Donations
    addDonation,
    updateDonation,
    deleteDonation,
    // Dépenses
    addExpense,
    updateExpense,
    deleteExpense,
    // Journal de l'église
    addJournalEntry,
    updateJournalEntry,
    deleteJournalEntry,
    // Programmes
    addProgramme,
    updateProgramme,
    deleteProgramme,
    // Utilisateurs
    approveUser,
    rejectUser,
    updateUserRole,
    deleteUser,
    updateUser,
    addUser,
    // Fihirana
    addFihirana,
    updateFihirana,
    deleteFihirana
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export default DataContext;